import { getCustomizationProfiles } from '@/api/goaldetail'
import { getContentReviewProfiles } from '@/contentprofile/api'
import { CUSTOMIZATIONS } from './customizationdemo.catalog'
import { readStoredSelections, writeStoredSelections } from './customizationdemo.storage'

export default {
  namespaced: true,
  state: {
    selectionByCustomization: readStoredSelections(),
    availableProfileIdsByCustomization: {},
    fetching: false,
    error: null
  },
  mutations: {
    setSelection(state, { customization, profileId }) {
      state.selectionByCustomization = {
        ...state.selectionByCustomization,
        [customization]: profileId || null
      }
      writeStoredSelections(state.selectionByCustomization)
    },
    clearAllSelections(state) {
      state.selectionByCustomization = {}
      writeStoredSelections(state.selectionByCustomization)
    },
    setAvailableProfileIds(state, availableProfileIdsByCustomization) {
      state.availableProfileIdsByCustomization = availableProfileIdsByCustomization
    },
    dropSelections(state, customizations) {
      state.selectionByCustomization = { ...state.selectionByCustomization }
      customizations.forEach((customization) => {
        state.selectionByCustomization[customization] = null
      })
      writeStoredSelections(state.selectionByCustomization)
    },
    setFetching(state, flag) {
      state.fetching = flag
    },
    setError(state, error) {
      state.error = error
    }
  },
  actions: {
    // A category whose profile carries no customization would demo nothing at all, so the picker
    // checks up front and disables those options rather than failing silently. Each customization
    // is checked against its own backend: a profile that has a review rubric but no goal prompt is
    // available to demo under Auto-Review and not under Goal Customizations.
    async fetchAvailability({ state, commit, dispatch, rootGetters }) {
      commit('setFetching', true)
      commit('setError', null)

      try {
        const betaMode = !!rootGetters['visibility/betaVisibility']
        const [customizableGoalProfileIds, contentReviewProfiles] = await Promise.all([
          getCustomizationProfiles(betaMode),
          getContentReviewProfiles()
        ])
        const availableProfileIdsByCustomization = {
          [CUSTOMIZATIONS.GOALS]: customizableGoalProfileIds,
          [CUSTOMIZATIONS.AUTO_REVIEW]: contentReviewProfiles
            .map((profile) => profile.content_profile)
            .filter(Boolean)
        }

        commit('setAvailableProfileIds', availableProfileIdsByCustomization)
        await dispatch('dropStaleSelections', availableProfileIdsByCustomization)
      } catch (error) {
        commit('setError', error)
      } finally {
        commit('setFetching', false)
      }
    },
    // A selection restored from a previous page load can name a profile the backend no longer
    // reports — a customization unreleased in Airtable, or a refreshed cache. Disabling the option
    // is not enough: the selection would still be sent as content_profile and the page would keep
    // asking for a customization that is gone. Drop those and refresh so the page returns to
    // normal, leaving customizations the backend said nothing about untouched.
    async dropStaleSelections({ state, commit, dispatch }, availableProfileIdsByCustomization) {
      const stale = Object.entries(availableProfileIdsByCustomization)
        .filter(([customization, profileIds]) => {
          const selected = state.selectionByCustomization[customization]
          return selected && !profileIds.includes(selected)
        })
        .map(([customization]) => customization)

      if (!stale.length) return

      commit('dropSelections', stale)
      await Promise.all(stale.map((customization) => dispatch('refreshCustomization', customization)))
    },
    async selectProfile({ commit, dispatch }, { customization, profileId }) {
      commit('setSelection', { customization, profileId })
      await dispatch('refreshCustomization', customization)
    },
    turnOff({ dispatch }, customization) {
      return dispatch('selectProfile', { customization, profileId: null })
    },
    async turnOffAll({ commit, dispatch }) {
      commit('clearAllSelections')
      await Promise.all(
        Object.values(CUSTOMIZATIONS).map((customization) =>
          dispatch('refreshCustomization', customization)
        )
      )
    },
    refreshCustomization({ dispatch }, customization) {
      if (customization === CUSTOMIZATIONS.GOALS) {
        return dispatch('goaldetail/refetchCurrentPage', null, { root: true })
      }
      if (customization === CUSTOMIZATIONS.AUTO_REVIEW) {
        return dispatch('contentProfile/fetchContentReviewCustomization', null, { root: true })
      }
      return Promise.resolve()
    }
  },
  getters: {
    demoProfileFor(state) {
      return (customization) => state.selectionByCustomization[customization] || null
    },
    // Only goals and auto-review have a backend that can report what is customizable. For the
    // rest there is nothing to check, so a category is not annotated as unavailable on a guess —
    // the page gate is what disables those sections.
    isProfileAvailable(state) {
      return (customization, profileId) => {
        const checked = state.availableProfileIdsByCustomization[customization]
        return checked ? checked.includes(profileId) : true
      }
    },
    activeDemoCount(state) {
      return Object.values(state.selectionByCustomization).filter(Boolean).length
    },
    isDemoActive(state, getters) {
      return getters.activeDemoCount > 0
    }
  }
}
